import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import { useProducts } from './useProducts';
import type {
  ProductGroupSummary,
  ProductGroupDetail,
  DivergenceStats,
  RestockingGroup,
  CreateProductGroupDto,
  UpdateProductGroupDto,
  SetStockTargetDto,
} from '../types';

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch all product groups for a household.
 *
 * @param householdId - The household ID
 * @param params - Optional query params (search, category)
 */
async function getProductGroups(
  householdId: string,
  params?: Record<string, unknown>
): Promise<ProductGroupSummary[]> {
  const searchParams = new URLSearchParams();
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        searchParams.set(key, String(value));
      }
    }
  }
  const query = searchParams.toString();
  const url = `/households/${householdId}/product-groups${query ? `?${query}` : ''}`;
  return apiFetch<ProductGroupSummary[]>(url);
}

/**
 * Fetch a single product group with its members.
 *
 * @param householdId - The household ID
 * @param groupId - The product group ID
 */
async function getProductGroup(householdId: string, groupId: string): Promise<ProductGroupDetail> {
  return apiFetch<ProductGroupDetail>(`/households/${householdId}/product-groups/${groupId}`);
}

/**
 * Create a new product group.
 *
 * @param householdId - The household ID
 * @param data - Creation payload
 */
async function createProductGroup(
  householdId: string,
  data: CreateProductGroupDto
): Promise<ProductGroupSummary> {
  return apiFetch<ProductGroupSummary>(`/households/${householdId}/product-groups`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing product group.
 *
 * @param householdId - The household ID
 * @param groupId - The product group ID
 * @param data - Update payload
 */
async function updateProductGroup(
  householdId: string,
  groupId: string,
  data: UpdateProductGroupDto
): Promise<ProductGroupSummary> {
  return apiFetch<ProductGroupSummary>(`/households/${householdId}/product-groups/${groupId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a product group.
 *
 * @param householdId - The household ID
 * @param groupId - The product group ID
 */
async function deleteProductGroup(householdId: string, groupId: string): Promise<void> {
  await apiFetch(`/households/${householdId}/product-groups/${groupId}`, {
    method: 'DELETE',
  });
}

/**
 * Add a product as a member of a product group.
 *
 * @param householdId - The household ID
 * @param groupId - The product group ID
 * @param productId - The product to add
 */
async function addGroupMember(
  householdId: string,
  groupId: string,
  productId: string
): Promise<void> {
  await apiFetch(`/households/${householdId}/product-groups/${groupId}/members`, {
    method: 'POST',
    body: JSON.stringify({ productId }),
  });
}

/**
 * Remove a product from a product group.
 *
 * @param householdId - The household ID
 * @param groupId - The product group ID
 * @param productId - The product to remove
 */
async function removeGroupMember(
  householdId: string,
  groupId: string,
  productId: string
): Promise<void> {
  await apiFetch(`/households/${householdId}/product-groups/${groupId}/members/${productId}`, {
    method: 'DELETE',
  });
}

/**
 * Set the stock target for a product group.
 *
 * @param householdId - The household ID
 * @param groupId - The product group ID
 * @param data - Target quantity and unit
 */
async function setStockTarget(
  householdId: string,
  groupId: string,
  data: SetStockTargetDto
): Promise<ProductGroupSummary> {
  return apiFetch<ProductGroupSummary>(
    `/households/${householdId}/product-groups/${groupId}/target`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Fetch product groups that need restocking (current stock < target).
 *
 * @param householdId - The household ID
 */
async function getNeedsRestocking(householdId: string): Promise<RestockingGroup[]> {
  return apiFetch<RestockingGroup[]>(`/households/${householdId}/product-groups/needs-restocking`);
}

/**
 * Fetch divergence statistics for the household's product groups.
 *
 * @param householdId - The household ID
 */
async function getDivergenceStats(householdId: string): Promise<DivergenceStats> {
  return apiFetch<DivergenceStats>(`/households/${householdId}/product-groups/divergence-stats`);
}

/**
 * Resync a single diverged group back to its global template.
 *
 * @param householdId - The household ID
 * @param groupId - The product group ID
 */
async function resyncGroup(householdId: string, groupId: string): Promise<void> {
  await apiFetch(`/households/${householdId}/product-groups/${groupId}/resync`, {
    method: 'POST',
  });
}

/**
 * Resync all product groups in the household back to global templates.
 *
 * @param householdId - The household ID
 */
async function resyncAllGroups(householdId: string): Promise<void> {
  await apiFetch(`/households/${householdId}/product-groups/resync-all`, {
    method: 'POST',
  });
}

/**
 * Split a product group by moving selected members to a new custom group.
 *
 * @param householdId - The household ID
 * @param groupId - The source group ID
 * @param newGroupName - Name for the new group
 * @param productIds - Product IDs to move
 */
async function splitGroup(
  householdId: string,
  groupId: string,
  newGroupName: string,
  productIds: string[]
): Promise<ProductGroupSummary> {
  return apiFetch<ProductGroupSummary>(
    `/households/${householdId}/product-groups/${groupId}/split`,
    {
      method: 'POST',
      body: JSON.stringify({ newGroupName, productIds }),
    }
  );
}

/**
 * Dismiss an auto-group suggestion for a product.
 *
 * @param householdId - The household ID
 * @param groupId - The product group ID
 * @param productId - The product to dismiss from auto-grouping
 */
async function dismissAutoGroup(
  householdId: string,
  groupId: string,
  productId: string
): Promise<void> {
  return apiFetch(`/households/${householdId}/product-groups/${groupId}/dismiss-auto-group`, {
    method: 'POST',
    body: JSON.stringify({ productId }),
  });
}

/**
 * Merge a source group into a target group.
 *
 * @param householdId - The household ID
 * @param sourceGroupId - The group to merge from (will be deleted)
 * @param targetGroupId - The group to merge into
 */
async function mergeGroups(
  householdId: string,
  sourceGroupId: string,
  targetGroupId: string
): Promise<void> {
  await apiFetch(`/households/${householdId}/product-groups/${sourceGroupId}/merge`, {
    method: 'POST',
    body: JSON.stringify({ targetGroupId }),
  });
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch product groups for a household.
 * Supports optional search and category filters.
 *
 * @param householdId - The household ID
 * @param params - Optional search/category query params
 * @param options - Optional query options (e.g., enabled)
 */
export function useProductGroups(
  householdId: string,
  params?: Record<string, unknown>,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.productGroups.list(householdId, params),
    queryFn: () => getProductGroups(householdId, params),
    enabled: (options?.enabled ?? true) && !!householdId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to fetch a single product group with its members.
 *
 * @param householdId - The household ID
 * @param groupId - The product group ID
 */
export function useProductGroup(householdId: string, groupId: string) {
  return useQuery({
    queryKey: queryKeys.productGroups.detail(householdId, groupId),
    queryFn: () => getProductGroup(householdId, groupId),
    enabled: !!householdId && !!groupId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to fetch product groups that need restocking.
 *
 * @param householdId - The household ID
 */
export function useNeedsRestocking(householdId: string) {
  return useQuery({
    queryKey: queryKeys.productGroups.needsRestocking(householdId),
    queryFn: () => getNeedsRestocking(householdId),
    enabled: !!householdId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to fetch divergence statistics for the household's product groups.
 *
 * @param householdId - The household ID
 */
export function useDivergenceStats(householdId: string) {
  return useQuery({
    queryKey: queryKeys.productGroups.divergenceStats(householdId),
    queryFn: () => getDivergenceStats(householdId),
    enabled: !!householdId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to create a new product group.
 * Invalidates the household's product group cache on success.
 */
export function useCreateProductGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, data }: { householdId: string; data: CreateProductGroupDto }) =>
      createProductGroup(householdId, data),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productGroups.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to update an existing product group.
 * Invalidates the household's product group cache on success.
 */
export function useUpdateProductGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      groupId,
      data,
    }: {
      householdId: string;
      groupId: string;
      data: UpdateProductGroupDto;
    }) => updateProductGroup(householdId, groupId, data),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productGroups.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to delete a product group.
 * Invalidates the household's product group cache on success.
 */
export function useDeleteProductGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, groupId }: { householdId: string; groupId: string }) =>
      deleteProductGroup(householdId, groupId),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productGroups.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to add a product as a member of a product group.
 * Invalidates the household's product group cache on success.
 */
export function useAddGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      groupId,
      productId,
    }: {
      householdId: string;
      groupId: string;
      productId: string;
    }) => addGroupMember(householdId, groupId, productId),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productGroups.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to remove a product from a product group.
 * Invalidates the household's product group cache on success.
 */
export function useRemoveGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      groupId,
      productId,
    }: {
      householdId: string;
      groupId: string;
      productId: string;
    }) => removeGroupMember(householdId, groupId, productId),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productGroups.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to set the stock target for a product group.
 * Invalidates the household's product group cache on success.
 */
export function useSetStockTarget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      groupId,
      data,
    }: {
      householdId: string;
      groupId: string;
      data: SetStockTargetDto;
    }) => setStockTarget(householdId, groupId, data),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productGroups.byHousehold(householdId),
      });
      // Also invalidate items so isBelowTarget recalculates in consolidated view
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to resync a single diverged group back to its global template.
 * Invalidates the household's product group cache on success.
 */
export function useResyncGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, groupId }: { householdId: string; groupId: string }) =>
      resyncGroup(householdId, groupId),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productGroups.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to resync all product groups in the household back to global templates.
 * Invalidates the household's product group cache on success.
 */
export function useResyncAllGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId }: { householdId: string }) => resyncAllGroups(householdId),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productGroups.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to split a product group into two.
 * Creates a new custom group with the selected members.
 * Invalidates the household's product group cache on success.
 */
export function useSplitGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      groupId,
      newGroupName,
      productIds,
    }: {
      householdId: string;
      groupId: string;
      newGroupName: string;
      productIds: string[];
    }) => splitGroup(householdId, groupId, newGroupName, productIds),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productGroups.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to merge a source group into a target group.
 * Moves all members from source to target and deletes the source.
 * Invalidates the household's product group cache on success.
 */
export function useMergeGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      sourceGroupId,
      targetGroupId,
    }: {
      householdId: string;
      sourceGroupId: string;
      targetGroupId: string;
    }) => mergeGroups(householdId, sourceGroupId, targetGroupId),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productGroups.byHousehold(householdId),
      });
    },
  });
}

/**
 * Hook to dismiss an auto-group suggestion for a product.
 * Invalidates the household's product group cache on success.
 */
export function useDismissAutoGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      householdId,
      groupId,
      productId,
    }: {
      householdId: string;
      groupId: string;
      productId: string;
    }) => dismissAutoGroup(householdId, groupId, productId),
    onSuccess: (_, { householdId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productGroups.byHousehold(householdId),
      });
    },
  });
}

// ============================================================================
// Unified Search Hook
// ============================================================================

/**
 * Hook that searches both product groups and products simultaneously.
 *
 * Groups are returned first, sorted by exact prefix match then contains.
 * Products are annotated with `groupName` when they are members of a returned group.
 *
 * @param householdId - The household ID
 * @param query - The search query (minimum 2 characters to activate)
 */
export function useUnifiedSearch(householdId: string, query: string) {
  const groupsQuery = useProductGroups(
    householdId,
    { search: query },
    { enabled: query.length >= 2 }
  );
  const productsQuery = useProducts(query.length >= 2 ? { name: query } : { name: '' }, {
    enabled: query.length >= 2,
  });

  if (query.length < 2) {
    return { groups: [], products: [], isLoading: false };
  }

  const isLoading = groupsQuery.isLoading || productsQuery.isLoading;

  const rawGroups = groupsQuery.data ?? [];
  const rawProducts = productsQuery.data ?? [];

  // Sort groups: exact prefix match first, then contains
  const lowerQuery = query.toLowerCase();
  const groups = [...rawGroups].sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();
    const aPrefix = aName.startsWith(lowerQuery);
    const bPrefix = bName.startsWith(lowerQuery);
    if (aPrefix && !bPrefix) return -1;
    if (!aPrefix && bPrefix) return 1;
    return 0;
  });

  // Products returned as-is; group membership annotation deferred to a future API enhancement
  const products = rawProducts.map((product) => ({
    ...product,
    groupName: null as string | null,
  }));

  return { groups, products, isLoading };
}
